import { Component, Input } from '@angular/core';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-case-general-detail',
  imports: [
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    EmptyStateComponent,
    TableComponent,
    ColumnComponent,
    LoadingPanelComponent,
  ],
  template: `
    <div
      class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4 tw-pt-6"
    >
      <div class="tw-w-full xl:tw-w-1/3">
        <app-card label="Service Information">
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
            [loadingHeight]="150"
          >
            <ng-template #loaded>
              <app-detail-panel [dataSource]="dataSource">
                <app-detail-item label="Service Type" name="serviceType" />
                <app-detail-item label="Status" name="status" />
                <app-detail-item label="Assigned To" name="assignedTo" />
                <app-detail-item label="Supervisor" name="supervisor" />
                <app-detail-item label="Billing Type" name="billingType" />
              </app-detail-panel>
            </ng-template>
          </app-loading-panel>
        </app-card>
      </div>
      <div class="tw-w-full xl:tw-w-1/3">
        <app-card label="Billing Override">
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
            [loadingHeight]="100"
          >
            <ng-template #loaded>
              @if (hasBillingOverride) {
                <app-detail-panel [dataSource]="dataSource?.billingOverride">
                  <app-detail-item label="Reason" name="reason" />
                  <app-detail-item label="Override by" name="overrideBy" />
                  <app-detail-item
                    label="Override Date/Time"
                    name="overrideDateTime"
                    format="dt"
                  />
                </app-detail-panel>
              } @else {
                <app-empty-state
                  height="100px"
                  icon="fa-light fa-file-invoice-dollar"
                  label="No Billing Override"
                  message="There is no billing override information"
                />
              }
            </ng-template>
          </app-loading-panel>
        </app-card>
      </div>
      <div class="tw-w-full xl:tw-w-1/3">
        <app-card label="Closure Details">
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
            [loadingHeight]="120"
          >
            <ng-template #loaded>
              @if (hasClosureDetails) {
                <app-detail-panel [dataSource]="dataSource?.closureDetails">
                  <app-detail-item
                    label="Closure Reason"
                    name="closureReason"
                  />
                  <app-detail-item
                    label="Closure SubReason"
                    name="closureSubReason"
                  />
                  <app-detail-item
                    label="Date Employee Notified Of Closure"
                    name="dateNotified"
                    format="dt"
                  />
                  <app-detail-item label="How notified" name="howNotified" />
                </app-detail-panel>
              } @else {
                <app-empty-state
                  height="120px"
                  icon="fa-light fa-folder-closed"
                  label="No Closure Details"
                  message="There is no closure information available"
                />
              }
            </ng-template>
          </app-loading-panel>
        </app-card>
      </div>
    </div>
    <div
      class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4"
    >
      <div class="tw-w-full xl:tw-w-2/3">
        <app-card label="Case Impacts">
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
            [loadingHeight]="120"
          >
            <ng-template #loaded>
              <app-table
                [dataSource]="dataSource?.caseImpacts"
                [scrollable]="false"
                recordLabel="Case Impact"
              >
                <app-column
                  label="Recorded Date"
                  name="recordedDate"
                  format="d"
                />
                <app-column label="Description" name="description" />
              </app-table>
            </ng-template>
          </app-loading-panel>
        </app-card>
      </div>
      <div class="tw-w-full xl:tw-w-1/3">
        <app-card label="Open Periods">
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
            [loadingHeight]="120"
          >
            <ng-template #loaded>
              <app-table
                [dataSource]="dataSource?.openPeriods"
                [scrollable]="false"
                recordLabel="Open Period"
              >
                <app-column label="Start Date" name="startDate" format="d" />
                <app-column label="End Date" name="endDate" format="d" />
              </app-table>
            </ng-template>
          </app-loading-panel>
        </app-card>
      </div>
    </div>
  `,
  styles: [],
})
export class CaseGeneralDetailComponent {
  @Input() dataSource: any;
  @Input() loading = false;

  get hasBillingOverride(): boolean {
    const bo = this.dataSource?.billingOverride;
    if (!bo) return false;
    return !!(bo.reason || bo.overrideBy || bo.overrideDateTime);
  }

  get hasClosureDetails(): boolean {
    const cd = this.dataSource?.closureDetails;
    if (!cd) return false;
    return !!(
      cd.closureReason ||
      cd.closureSubReason ||
      cd.dateNotified ||
      cd.howNotified
    );
  }
}
